import { useRef } from 'react';
import { useSnapshot } from 'valtio';

import { HAND_OPTIONS } from '@cartwhl/common/types';
import { getResourcesUrl } from '@cartwhl/common/utils';
import Checkbox from '@cartwhl/ui/atoms/Checkbox';
import Text from '@cartwhl/ui/base/Text';
import ChevronRightIcon from '@cartwhl/ui/icons/ChevronRightIcon';
import SubPanel from '@cartwhl/ui/layout/SubPanel';
import AnimationPreview from '@cartwhl/ui/molecules/AnimationPreview';
import SubPanelButton from '@cartwhl/ui/molecules/SubPanelButton';

import { characterPickerStore, sceneSettingsStore, uiStore } from 'stores';

import { HAND_PICKER_SUBPANEL_KEY } from '../constants';
import { getHandDisplayName } from '../utils';

export default function HandPicker() {
  const ref = useRef(null);
  const { subPanelKey } = useSnapshot(uiStore.state);
  const isOpen = subPanelKey === HAND_PICKER_SUBPANEL_KEY;

  const { handOptions } = useSnapshot(characterPickerStore.state);
  const {
    characterSettings: { staticHands },
  } = useSnapshot(sceneSettingsStore.state);

  const handImageName =
    staticHands === HAND_OPTIONS.DEFAULT ? 'rps_hand' : staticHands;

  const baseURL = getResourcesUrl(false);
  return (
    <>
      <SubPanelButton
        isOpen={isOpen}
        onClick={() => {
          uiStore.setSubPanelKey(isOpen ? '' : HAND_PICKER_SUBPANEL_KEY);
        }}
        ref={ref}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-4">
            <div className="-my-2 -ml-2">
              <img
                className="aspect-square w-[60px] rounded-lg object-cover"
                src={`${baseURL}/images/static_hands/${handImageName}.webp`}
                alt={staticHands}
              />
            </div>
            <div className="text-left">
              <Text variant="studio/body/medium/sm" as="h2">
                Hands
              </Text>
              <Text
                variant="studio/body/medium/sm"
                as="div"
                className="text-gray-45"
              >
                {getHandDisplayName(staticHands)}
              </Text>
            </div>
          </div>
          <ChevronRightIcon className="text-gray-45" />
        </div>
      </SubPanelButton>
      {isOpen && (
        <SubPanel
          id={HAND_PICKER_SUBPANEL_KEY}
          elementToTrack={ref.current}
          closePanel={() => uiStore.setSubPanelKey('')}
          subPanelKey={subPanelKey}
        >
          <>
            <div
              className={`mb-4 flex justify-center rounded-lg border-transparent bg-[#ECF4FF] py-4 dark:bg-[#282d33] ${
                staticHands === HAND_OPTIONS.DEFAULT
                  ? 'shadow-outline-blue-2'
                  : ''
              }`}
            >
              <Checkbox
                id="animated-hands"
                label="Use animated hands"
                color="settings"
                variant="checkbox"
                checked={staticHands === HAND_OPTIONS.DEFAULT}
                setChecked={(checked) => {
                  sceneSettingsStore.set(
                    'characterSettings',
                    'staticHands',
                    checked ? HAND_OPTIONS.DEFAULT : HAND_OPTIONS.RELAXED,
                  );
                }}
              />
            </div>
            <div className="grid grid-cols-4 gap-2">
              {handOptions.map((hand) => {
                return (
                  <AnimationPreview
                    key={hand}
                    active={staticHands === hand}
                    onClick={() => {
                      sceneSettingsStore.set(
                        'characterSettings',
                        'staticHands',
                        hand,
                      );
                    }}
                    mediaPath={`${baseURL}/images/static_hands/${hand}.webp`}
                    mediaType="image"
                  />
                );
              })}
            </div>
          </>
        </SubPanel>
      )}
    </>
  );
}
